import { ChangeDetectionStrategy, Component, OnInit, inject, input, signal } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Option } from '@interfaces/common/option';
import { CompanySectorHttpService } from '@services/http/company/company-sector-http.service';
import { SharedModule } from '@shared/shared.module';
import { first } from 'rxjs/operators';

/** Valor da opção "Sem setor" no select (o cadastro manda `null`, ou `CLEAR_COMPANY_SECTOR_ID` na edição). */
export const NO_COMPANY_SECTOR = '';

/**
 * `Guid.Empty`: no PUT da empresa é o que limpa o setor. Nulo mantém o setor atual, porque tela antiga que não manda
 * o campo apagaria o setor de toda empresa editada.
 */
export const CLEAR_COMPANY_SECTOR_ID = '00000000-0000-0000-0000-000000000000';

/**
 * Valor do setor para o corpo do POST/PUT da empresa a partir do valor do select.
 * @param selected Valor do select (id do setor, ou vazio para "Sem setor").
 * @param isEditing Edição (PUT) ou cadastro novo (POST).
 */
export function toCompanySectorPayload(
  selected: string | null | undefined,
  isEditing: boolean
): string | null {
  if (selected) return selected;
  return isEditing ? CLEAR_COMPANY_SECTOR_ID : null;
}

/**
 * Campo "Setor" do cadastro da empresa (card 3642): escolha na lista padronizada e global de setores
 * (Back-office › Setores). Opcional. Não confundir com a "Atividade principal", o texto do CNAE.
 */
@Component({
  selector: 'app-company-sector-field',
  imports: [SharedModule, ReactiveFormsModule],
  templateUrl: './company-sector-field.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompanySectorFieldComponent implements OnInit {
  /** Formulário da empresa (o campo usa `controlName` dentro dele). */
  readonly form = input.required<FormGroup>();
  /** Nome do controle do setor no formulário. */
  readonly controlName = input.required<string>();

  protected readonly tooltip =
    'Opcional. Setor padronizado, o mesmo para todos os clientes, usado para agrupar e filtrar empresas. ' +
    'Setor que não está na lista é cadastrado por um administrador da Legatus em Back-office › Setores.';
  protected readonly options = signal<Option[]>([{ label: 'Sem setor', value: NO_COMPANY_SECTOR }]);
  protected readonly loading = signal(true);
  protected readonly loadFailed = signal(false);

  private readonly sectorHttp = inject(CompanySectorHttpService);

  ngOnInit(): void {
    this.sectorHttp
      .listOptions()
      .pipe(first())
      .subscribe({
        next: options => {
          this.options.set([{ label: 'Sem setor', value: NO_COMPANY_SECTOR }, ...options]);
          this.loading.set(false);
        },
        error: () => {
          this.loadFailed.set(true);
          this.loading.set(false);
        },
      });
  }
}
